package org.gymly.ui.admin

import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.BoxWithConstraints
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.heightIn
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.widthIn
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.verticalScroll
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material3.AlertDialog
import androidx.compose.material3.Button
import androidx.compose.material3.ButtonDefaults
import androidx.compose.material3.CircularProgressIndicator
import androidx.compose.material3.OutlinedButton
import androidx.compose.material3.OutlinedTextField
import androidx.compose.material3.OutlinedTextFieldDefaults
import androidx.compose.material3.Text
import androidx.compose.material3.TextButton
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.SpanStyle
import androidx.compose.ui.text.buildAnnotatedString
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.withStyle
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import kotlinx.coroutines.delay
import kotlinx.coroutines.launch
import org.gymly.data.AdminRepo
import org.gymly.model.AdminApprovalRequest
import org.gymly.model.AdminUser
import org.gymly.model.canApproveAnything
import org.gymly.theme.AdminColors
import org.gymly.util.userMessage

private enum class ApprovalsView { Pending, Mine }

@Composable
fun AdminApprovalsPanel(
    user: AdminUser,
    modifier: Modifier = Modifier,
) {
    val scope = rememberCoroutineScope()
    val canApprove = !user.permissionsKnown || user.canApproveAnything()
    var view by remember {
        mutableStateOf(if (canApprove) ApprovalsView.Pending else ApprovalsView.Mine)
    }
    var pending by remember { mutableStateOf<List<AdminApprovalRequest>>(emptyList()) }
    var mine by remember { mutableStateOf<List<AdminApprovalRequest>>(emptyList()) }
    var loading by remember { mutableStateOf(true) }
    var error by remember { mutableStateOf<String?>(null) }
    var success by remember { mutableStateOf<String?>(null) }
    var busyId by remember { mutableStateOf<String?>(null) }
    val bumpData = LocalAdminBumpDataRevision.current
    var rejectTarget by remember { mutableStateOf<AdminApprovalRequest?>(null) }
    var rejectNote by remember { mutableStateOf("") }

    fun reload() {
        scope.launch {
            loading = true
            error = null
            try {
                if (canApprove) {
                    pending = AdminRepo.api.listPendingApprovals()
                } else {
                    pending = emptyList()
                }
                mine = AdminRepo.api.listMyApprovals()
            } catch (e: Exception) {
                error = e.userMessage("No se pudieron cargar las aprobaciones.")
                pending = emptyList()
                mine = emptyList()
            } finally {
                loading = false
            }
        }
    }

    LaunchedEffect(canApprove) { reload() }

    // Refresco silencioso: las solicitudes nuevas aparecen sin recargar la página.
    // No activa el indicador de carga ni borra mensajes; se pausa mientras se aprueba o rechaza.
    LaunchedEffect(canApprove) {
        while (true) {
            delay(15_000)
            if (busyId == null && rejectTarget == null) {
                runCatching {
                    pending = if (canApprove) AdminRepo.api.listPendingApprovals() else emptyList()
                    mine = AdminRepo.api.listMyApprovals()
                }
            }
        }
    }

    val items = if (view == ApprovalsView.Pending) pending else mine

    // Scroll propio: la pestaña no desplaza y las solicitudes de abajo quedaban cortadas.
    Column(
        modifier.fillMaxWidth().verticalScroll(rememberScrollState()),
        verticalArrangement = Arrangement.spacedBy(10.dp),
    ) {
        Text("Aprobaciones", fontWeight = FontWeight.Bold, fontSize = 16.sp, color = AdminColors.Text)
        Text(
            if (canApprove) {
                "Revisa solicitudes pendientes o consulta las que tú enviaste."
            } else {
                "Aquí ves el estado de lo que pediste aprobar (pendiente, aprobada o rechazada)."
            },
            color = AdminColors.TextMuted,
            fontSize = 12.sp,
        )
        Row(horizontalArrangement = Arrangement.spacedBy(8.dp)) {
            if (canApprove) {
                ApprovalsChip(
                    label = "Pendientes (${pending.size})",
                    selected = view == ApprovalsView.Pending,
                    onClick = { view = ApprovalsView.Pending },
                )
            }
            ApprovalsChip(
                label = "Mis solicitudes (${mine.size})",
                selected = view == ApprovalsView.Mine,
                onClick = { view = ApprovalsView.Mine },
            )
        }
        error?.let { Text(it, color = AdminColors.Error, fontSize = 13.sp) }
        success?.let { Text(it, color = AdminColors.Primary, fontSize = 13.sp) }
        when {
            loading -> CircularProgressIndicator(color = AdminColors.Primary, modifier = Modifier.padding(16.dp))
            items.isEmpty() -> Text(
                if (view == ApprovalsView.Pending) {
                    "No hay solicitudes pendientes para ti."
                } else {
                    "Aún no has enviado solicitudes de aprobación."
                },
                color = AdminColors.TextMuted,
                fontSize = 13.sp,
            )
            else -> items.forEach { req ->
                ApprovalCard(
                    req = req,
                    showActions = view == ApprovalsView.Pending && canApprove && req.status.equals("PENDING", true),
                    busy = busyId != null,
                    onApprove = {
                        if (busyId != null) return@ApprovalCard
                        scope.launch {
                            busyId = req.id
                            error = null
                            success = null
                            try {
                                AdminRepo.api.approveRequest(req.id)
                                success = "Solicitud aprobada."
                                // El cambio aprobado ya existe: las pantallas de gestión lo muestran sin esperar.
                                bumpData()
                                reload()
                            } catch (e: Exception) {
                                error = e.userMessage("No se pudo aprobar.")
                            } finally {
                                busyId = null
                            }
                        }
                    },
                    onReject = {
                        if (busyId != null) return@ApprovalCard
                        rejectNote = ""
                        rejectTarget = req
                    },
                )
            }
        }
    }

    rejectTarget?.let { target ->
        AlertDialog(
            onDismissRequest = { if (busyId == null) rejectTarget = null },
            title = { Text("Rechazar solicitud") },
            text = {
                Column {
                    Text(target.summary, fontSize = 13.sp, color = AdminColors.Text)
                    Spacer(Modifier.height(10.dp))
                    OutlinedTextField(
                        value = rejectNote,
                        onValueChange = { rejectNote = it },
                        modifier = Modifier.fillMaxWidth(),
                        singleLine = false,
                        minLines = 2,
                        placeholder = { Text("Motivo del rechazo", color = AdminColors.TextMuted) },
                        shape = RoundedCornerShape(10.dp),
                        colors = OutlinedTextFieldDefaults.colors(
                            focusedBorderColor = AdminColors.Primary,
                            unfocusedBorderColor = AdminColors.Outline,
                        ),
                    )
                }
            },
            confirmButton = {
                Button(
                    onClick = {
                        val note = rejectNote.trim()
                        if (note.isBlank()) {
                            error = "Indica el motivo del rechazo."
                            return@Button
                        }
                        scope.launch {
                            busyId = target.id
                            error = null
                            success = null
                            try {
                                AdminRepo.api.rejectRequest(target.id, note)
                                rejectTarget = null
                                success = "Solicitud rechazada."
                                reload()
                            } catch (e: Exception) {
                                error = e.userMessage("No se pudo rechazar.")
                            } finally {
                                busyId = null
                            }
                        }
                    },
                    enabled = busyId == null,
                    colors = ButtonDefaults.buttonColors(
                        containerColor = AdminColors.Error,
                        contentColor = Color.White,
                    ),
                ) { Text(if (busyId == target.id) "…" else "Rechazar") }
            },
            dismissButton = {
                TextButton(onClick = { rejectTarget = null }, enabled = busyId == null) {
                    Text("Cancelar", color = AdminColors.TextMuted)
                }
            },
        )
    }
}

@Composable
private fun ApprovalsChip(label: String, selected: Boolean, onClick: () -> Unit) {
    Text(
        label,
        color = if (selected) AdminColors.OnBrand else AdminColors.Text,
        fontWeight = FontWeight.SemiBold,
        fontSize = 12.sp,
        modifier = Modifier
            .clip(RoundedCornerShape(20.dp))
            .background(if (selected) AdminColors.Primary else AdminColors.SurfaceContainer)
            .border(1.dp, if (selected) AdminColors.Primary else AdminColors.Outline, RoundedCornerShape(20.dp))
            .clickable(onClick = onClick)
            .heightIn(min = 40.dp)
            .padding(horizontal = 14.dp, vertical = 10.dp),
    )
}

@Composable
private fun ApprovalCard(
    req: AdminApprovalRequest,
    showActions: Boolean,
    busy: Boolean,
    onApprove: () -> Unit,
    onReject: () -> Unit,
) {
    val statusColor = when (req.status.uppercase()) {
        "PENDING" -> Color(0xFFF59E0B)
        "APPROVED" -> Color(0xFF2E7D32)
        "REJECTED" -> AdminColors.Error
        else -> AdminColors.TextMuted
    }
    Column(
        Modifier
            .fillMaxWidth()
            .clip(RoundedCornerShape(12.dp))
            .border(1.dp, AdminColors.Outline.copy(alpha = 0.55f), RoundedCornerShape(12.dp))
            .background(AdminColors.Surface)
            .padding(12.dp),
    ) {
        // El tipo ocupa el espacio que sobra; el estado no se aprieta aunque el título sea largo.
        Row(
            Modifier.fillMaxWidth(),
            horizontalArrangement = Arrangement.SpaceBetween,
            verticalAlignment = Alignment.Top,
        ) {
            Text(
                req.typeLabelEs(),
                fontWeight = FontWeight.SemiBold,
                color = AdminColors.Primary,
                fontSize = 13.sp,
                modifier = Modifier.weight(1f).padding(end = 8.dp),
            )
            Text(
                req.statusLabelEs(),
                color = statusColor,
                fontWeight = FontWeight.Bold,
                fontSize = 12.sp,
                maxLines = 1,
            )
        }
        Spacer(Modifier.height(8.dp))
        ResumenSolicitud(req.summary)
        val who = buildString {
            if (!req.requestedByName.isNullOrBlank()) append("Solicitó: ${req.requestedByName}")
            if (!req.reviewedByName.isNullOrBlank()) {
                if (isNotEmpty()) append(" · ")
                append("Revisó: ${req.reviewedByName}")
            }
        }
        if (who.isNotBlank()) {
            Text(who, color = AdminColors.TextMuted, fontSize = 12.sp, modifier = Modifier.padding(top = 4.dp))
        }
        req.createdAt?.let {
            Text(it, color = AdminColors.TextMuted, fontSize = 12.sp, modifier = Modifier.padding(top = 2.dp))
        }
        if (!req.reviewNote.isNullOrBlank()) {
            Text(
                "Nota: ${req.reviewNote}",
                color = if (req.status.equals("REJECTED", true)) AdminColors.Error else AdminColors.TextMuted,
                fontSize = 12.sp,
                modifier = Modifier.padding(top = 6.dp),
            )
        }
        if (showActions) {
            Spacer(Modifier.height(10.dp))
            Row(
                horizontalArrangement = Arrangement.spacedBy(8.dp),
                verticalAlignment = Alignment.CenterVertically,
            ) {
                Button(
                    onClick = onApprove,
                    enabled = !busy,
                    shape = RoundedCornerShape(10.dp),
                    colors = ButtonDefaults.buttonColors(
                        containerColor = AdminColors.Primary,
                        contentColor = AdminColors.OnBrand,
                    ),
                    modifier = Modifier.widthIn(min = 120.dp, max = 240.dp).heightIn(min = 48.dp),
                ) {
                    Text(if (busy) "…" else "Aprobar")
                }
                OutlinedButton(
                    onClick = onReject,
                    enabled = !busy,
                    shape = RoundedCornerShape(10.dp),
                    modifier = Modifier.widthIn(min = 120.dp, max = 240.dp).heightIn(min = 48.dp),
                ) {
                    Text("Rechazar", color = AdminColors.Error)
                }
            }
        }
    }
}

/**
 * Resumen de la solicitud: el título arriba y, debajo, un renglón por dato.
 * Cada renglón "Etiqueta: valor" muestra la etiqueta en gris y el valor en negro, para leerlo de un vistazo en el celular.
 */
@Composable
private fun ResumenSolicitud(resumen: String) {
    val lineas = resumen.lines().filter { it.isNotBlank() }
    val titulo = lineas.firstOrNull()
    val datos = lineas.drop(1)
    // Columnas según el ancho de la tarjeta: 3 en pantallas anchas, 2 en tabletas y 1 en celular.
    BoxWithConstraints(Modifier.fillMaxWidth()) {
        val columnas = when {
            maxWidth >= 720.dp -> 3
            maxWidth >= 460.dp -> 2
            else -> 1
        }
        Column(verticalArrangement = Arrangement.spacedBy(6.dp)) {
            titulo?.let { LineaResumen(it, esTitulo = true) }
            datos.chunked(columnas).forEach { fila ->
                Row(Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.spacedBy(16.dp)) {
                    fila.forEach { linea ->
                        Box(Modifier.weight(1f)) { LineaResumen(linea, esTitulo = false) }
                    }
                    // Completa la última fila para que las columnas sigan alineadas.
                    repeat(columnas - fila.size) { Spacer(Modifier.weight(1f)) }
                }
            }
        }
    }
}

/** Un renglón del resumen: el título en negrita; en los datos, la etiqueta en gris y el valor en negro. */
@Composable
private fun LineaResumen(linea: String, esTitulo: Boolean) {
    val separador = linea.indexOf(": ")
    Text(
        buildAnnotatedString {
            if (!esTitulo && separador > 0) {
                withStyle(SpanStyle(color = AdminColors.TextMuted, fontWeight = FontWeight.SemiBold)) {
                    append(linea.substring(0, separador + 1))
                }
                append(linea.substring(separador + 1))
            } else {
                withStyle(SpanStyle(fontWeight = if (esTitulo) FontWeight.SemiBold else FontWeight.Normal)) {
                    append(linea)
                }
            }
        },
        color = AdminColors.Text,
        fontSize = 13.sp,
    )
}
